import { useEffect, useRef, useState } from 'preact/hooks';
import { formatIngredient, type IngredientItem, type RecipeStep } from '../../shared/recipe';
import { ErrorBox, Loading, Segmented } from '../components/ui';
import { api, type Card } from '../lib/api';
import { navigate } from '../lib/router';
import { prefs, setPref, useAsync } from '../lib/store';

interface Timer {
  id: number;
  label: string;
  endsAt: number;
  total: number;
  done: boolean;
}

const STOP = new Set(['and', 'the', 'with', 'for', 'into', 'from', 'fresh', 'large', 'small', 'medium', 'of', 'or', 'to', 'a', 'in']);

/** Ingredients whose main words appear in the step text. */
function relevantIngredients(step: RecipeStep, items: IngredientItem[]): IngredientItem[] {
  const text = step.text.toLowerCase();
  return items.filter((it) =>
    it.item
      .toLowerCase()
      .split(/[^\p{L}]+/u)
      .filter((w) => w.length > 2 && !STOP.has(w))
      .some((w) => new RegExp(`(^|[^\\p{L}])${w.replace(/(es|s)$/, '')}`, 'u').test(text)),
  );
}

function fmt(ms: number) {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h ? `${h}:` : ''}${String(m).padStart(h ? 2 : 1, '0')}:${String(sec).padStart(2, '0')}`;
}

function beep() {
  try {
    const ctx = new AudioContext();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.frequency.value = 880;
    g.gain.value = 0.2;
    o.start();
    o.stop(ctx.currentTime + 0.6);
  } catch {
    /* audio unavailable */
  }
}

export function CookMode({ id }: { id: string }) {
  const { data: card, error } = useAsync(() => api.get<Card>(`/cards/${id}`), [id]);
  const p = prefs.use();
  const [i, setI] = useState(0);
  const [timers, setTimers] = useState<Timer[]>([]);
  const [now, setNow] = useState(Date.now());
  const [wake, setWake] = useState<'on' | 'off' | 'unsupported'>('off');
  const touch = useRef<number | null>(null);
  const factor = Number(new URLSearchParams(location.search).get('f') ?? 1) || 1;

  // Screen Wake Lock keeps the phone awake while cooking; re-acquired when the tab becomes visible again.
  useEffect(() => {
    let lock: WakeLockSentinel | null = null;
    const acquire = async () => {
      if (!('wakeLock' in navigator)) return setWake('unsupported');
      try {
        lock = await navigator.wakeLock.request('screen');
        setWake('on');
        lock.addEventListener('release', () => setWake('off'));
      } catch {
        setWake('off');
      }
    };
    void acquire();
    const vis = () => document.visibilityState === 'visible' && acquire();
    document.addEventListener('visibilitychange', vis);
    return () => {
      document.removeEventListener('visibilitychange', vis);
      void lock?.release();
    };
  }, []);

  useEffect(() => {
    const prev = document.documentElement.dataset.theme;
    document.documentElement.dataset.theme = p.cookTheme;
    return () => void (document.documentElement.dataset.theme = prev ?? p.theme);
  }, [p.cookTheme]);

  useEffect(() => {
    if (!timers.some((t) => !t.done)) return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [timers]);

  useEffect(() => {
    let changed = false;
    const next = timers.map((t) => {
      if (!t.done && t.endsAt <= now) {
        changed = true;
        navigator.vibrate?.([300, 150, 300, 150, 600]);
        beep();
        if ('Notification' in window && Notification.permission === 'granted') new Notification('Timer done', { body: t.label, tag: `bakery-${t.id}` });
        return { ...t, done: true };
      }
      return t;
    });
    if (changed) setTimers(next);
  }, [now]);

  const steps: RecipeStep[] = card?.data?.steps ?? [];
  const items: IngredientItem[] = (card?.data?.ingredient_groups ?? []).flatMap((g: { items: IngredientItem[] }) => g.items);
  const go = (d: number) => setI((x) => Math.min(Math.max(0, x + d), Math.max(steps.length - 1, 0)));

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        go(1);
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        go(-1);
      } else if (e.key === 'Escape') navigate(`/c/${id}`);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [steps.length]);

  if (error) return <ErrorBox error={error} />;
  if (!card) return <Loading />;
  const step = steps[i];

  const startTimer = (min: number, label: string) => {
    if ('Notification' in window && Notification.permission === 'default') void Notification.requestPermission();
    setTimers((t) => [...t, { id: Date.now(), label, endsAt: Date.now() + min * 60_000, total: min * 60_000, done: false }]);
    setNow(Date.now());
  };

  return (
    <div class="cook" role="application" aria-label={`Bake mode: ${card.title}`}>
      <div>
        <div class="cook-top">
          <button class="dr-btn bk-ghost" onClick={() => navigate(`/c/${card.id}/${card.slug}`)} aria-label="Exit bake mode">
            ✕
          </button>
          <strong class="grow">{card.title}</strong>
          <span class="muted small" title="Screen stays awake while baking">
            {wake === 'on' ? 'Screen stays on' : ''}
          </span>
          <Segmented
            label="Theme"
            value={p.cookTheme}
            onChange={(v) => setPref('cookTheme', v)}
            options={[
              ['light', '☀'],
              ['dark', '☾'],
            ]}
          />
        </div>
        <div class="progress" aria-hidden="true">
          <div style={{ width: `${steps.length ? ((i + 1) / steps.length) * 100 : 0}%` }} />
        </div>
        {timers.length > 0 && (
          <div class="timers" style={{ padding: 'var(--bk-space-2) var(--bk-space-4)' }} aria-live="assertive">
            {timers.map((t) => (
              <span class={`timer-pill ${t.done ? 'done' : ''}`}>
                {t.done ? `✓ ${t.label}` : `${fmt(t.endsAt - now)} · ${t.label}`}
                <button aria-label={`Dismiss timer ${t.label}`} onClick={() => setTimers((x) => x.filter((y) => y.id !== t.id))}>
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div
        class="cook-step"
        onTouchStart={(e) => (touch.current = e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (touch.current === null) return;
          const dx = e.changedTouches[0].clientX - touch.current;
          if (Math.abs(dx) > 60) go(dx < 0 ? 1 : -1);
          touch.current = null;
        }}
      >
        {step ? (
          <>
            <p class="muted small">
              Step {i + 1} of {steps.length}
            </p>
            <p aria-live="polite">{step.text}</p>
            {step.timer_min ? (
              <button class="dr-btn" style={{ minHeight: 'var(--touch-cook)' }} onClick={() => startTimer(step.timer_min!, `Step ${i + 1}`)}>
                ⏱ Start {step.timer_min} min timer
              </button>
            ) : null}
            {relevantIngredients(step, items).length > 0 && (
              <ul class="cook-ings" aria-label="Ingredients for this step">
                {relevantIngredients(step, items).map((it) => (
                  <li>{formatIngredient(it, factor, p.units)}</li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <p>This recipe has no steps yet.</p>
        )}
      </div>

      <div class="cook-nav">
        <button class="dr-btn" onClick={() => go(-1)} disabled={i === 0}>
          ← Back
        </button>
        <span class="muted">
          {i + 1}/{steps.length || 1}
        </span>
        {i < steps.length - 1 ? (
          <button class="dr-btn dr-btn--primary" onClick={() => go(1)}>
            Next →
          </button>
        ) : (
          <button class="dr-btn dr-btn--primary" onClick={() => navigate(`/c/${card.id}/${card.slug}`)}>
            Done ✓
          </button>
        )}
      </div>
    </div>
  );
}
